import React, { useState } from "react";
import {
  Dialog,
  Input,
  TextArea,
  Button,
  Flex,
  Dropdown,
} from "@fluentui/react-northstar";
import { toast } from "react-toastify";
import { api } from "../../../../Assets/api/api";
import { useTranslation } from "react-i18next";
import "./SchoolBranchForm.css";
import { Select } from "antd";

export const CATEGORY_ENUM = Object.freeze({
  URBAN: "Urban",
  RURAL: "Rural",
  SEMI_URBAN: "Semi-Urban",
  ELITE: "Elite",
  NGO: "NGO",
  OTHER: "Other",
});

export const TYPE_ENUM = Object.freeze({
  PUBLIC: "Public",
  PRIVATE: "Private",
  INTERNATIONAL: "International",
});

const categoryOptions = Object.values(CATEGORY_ENUM);
const typeOptions = Object.values(TYPE_ENUM);

const SchoolBranchForm = ({
  open,
  onCancel,
  user,
  onSuccess,
  editingBranch,
  isEditMode = false,
}) => {
  const { t } = useTranslation();
  const translation = t("trustedNetwork").schools.branchForm;
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    email: "",
    contactPhone: "",
    type: "",
    category: "",
    description: "",
    city: "",
    country: "",
  });

  // Initialize form data when editing
  React.useEffect(() => {
    if (isEditMode && editingBranch) {
      setFormData({
        name: editingBranch.name || "",
        address: editingBranch.address || "",
        email: editingBranch.email || "",
        contactPhone: editingBranch.contactPhone || "",
        type: editingBranch.type || "",
        category: editingBranch.category || "",
        description: editingBranch.description || "",
        city: editingBranch.city || "",
        country: editingBranch.country || "",
      });
    } else if (!isEditMode) {
      // Reset form for create mode
      setFormData({
        name: "",
        address: "",
        email: "",
        contactPhone: "",
        type: "",
        category: "",
        description: "",
        city: "",
        country: "",
      });
    }
  }, [isEditMode, editingBranch, open]);

  //   const typeOptions = [
  //     { value: "Public", label: "Public" },
  //     { value: "Private", label: "Private" },
  //     { value: "International", label: "International" },
  //   ];

  //   const categoryOptions = [
  //     { value: "Urban", label: "Urban" },
  //     { value: "Rural", label: "Rural" },
  //     { value: "Semi-Urban", label: "Semi-Urban" },
  //     { value: "Elite", label: "Elite" },
  //     { value: "NGO", label: "NGO" },
  //     { value: "Other", label: "Other" },
  //   ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error(translation.validation.branchNameRequired);
      return;
    }

    setIsLoading(true);
    try {
      if (isEditMode && editingBranch) {
        // Update existing branch
        const response = await api.put(
          `/api/school/update-branch/${editingBranch.id}${user.slug}`,
          formData
        );

        if (response.status === 200) {
          toast.success("School branch updated successfully!");
          handleCancel();
          if (onSuccess) onSuccess();
        }
      } else {
        // Create new branch
        const parentEmail =
          user.mail ||
          user.schoolInfo?.ContactEmail ||
          user.ContactEmail ||
          user.email ||
          user.contactEmail;

        const payload = {
          ...formData,
          parentSchoolContactEmail: parentEmail,
        };

        const response = await api.post(
          `/api/school/create-branch${user.slug}`,
          payload
        );

        if (response.status === 201 || response.status === 200) {
          toast.success("School branch created successfully!");
          handleCancel();
          if (onSuccess) onSuccess();
        }
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} school branch:`,
        error
      );
      toast.error(
        error?.response?.data?.message ||
          `Failed to ${isEditMode ? "update" : "create"} school branch`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: "",
      address: "",
      email: "",
      contactPhone: "",
      type: "",
      category: "",
      description: "",
      city: "",
      country: "",
    });
    onCancel();
  };

  return (
    <Dialog
      open={open}
      onCancel={handleCancel}
      onConfirm={handleSubmit}
      header={isEditMode ? translation.editHeader : translation.createHeader}
      confirmButton={{
        content: isLoading
          ? isEditMode
            ? translation.updating
            : translation.creating
          : isEditMode
          ? translation.updateBranch
          : translation.createBranch,
        primary: true,
        loading: isLoading,
        disabled: isLoading || !formData.name.trim(),
      }}
      cancelButton={{
        content: translation.cancel,
        disabled: isLoading,
      }}
      className="school-branch-dialog"
      content={
        <Flex column gap="gap.medium" style={{ padding: "10px 0" }}>
          <div>
            <label
              className="fw6"
              style={{ marginBottom: "5px", display: "block" }}
            >
              {translation.branchNameLabel}
            </label>
            <Input
              placeholder={translation.branchNamePlaceholder}
              value={formData.name}
              onChange={(e, { value }) => handleInputChange("name", value)}
              disabled={isLoading}
              fluid
            />
          </div>

          <div>
            <label
              className="fw6"
              style={{ marginBottom: "5px", display: "block" }}
            >
              {translation.addressLabel}
            </label>
            <TextArea
              placeholder={translation.addressPlaceholder}
              value={formData.address}
              onChange={(e, { value }) => handleInputChange("address", value)}
              disabled={isLoading}
              fluid
            />
          </div>

          <div>
            <label
              className="fw6"
              style={{ marginBottom: "5px", display: "block" }}
            >
              {translation.emailLabel}
            </label>
            <Input
              placeholder={translation.emailPlaceholder}
              value={formData.email}
              onChange={(e, { value }) => handleInputChange("email", value)}
              disabled={isLoading}
              fluid
            />
          </div>

          <div>
            <label
              className="fw6"
              style={{ marginBottom: "5px", display: "block" }}
            >
              {translation.contactPhoneLabel}
            </label>
            <Input
              placeholder={translation.contactPhonePlaceholder}
              value={formData.contactPhone}
              onChange={(e, { value }) =>
                handleInputChange("contactPhone", value)
              }
              disabled={isLoading}
              fluid
            />
          </div>

          <div>
            <label
              className="fw6"
              style={{ marginBottom: "5px", display: "block" }}
            >
              {translation.cityLabel}
            </label>
            <Input
              placeholder={translation.cityPlaceholder}
              value={formData.city}
              onChange={(e, { value }) => handleInputChange("city", value)}
              disabled={isLoading}
              fluid
            />
          </div>

          <div>
            <label
              className="fw6"
              style={{ marginBottom: "5px", display: "block" }}
            >
              {translation.countryLabel}
            </label>
            <Input
              placeholder={translation.countryPlaceholder}
              value={formData.country}
              onChange={(e, { value }) => handleInputChange("country", value)}
              disabled={isLoading}
              fluid
            />
          </div>

          {/* <div>
            <label
              className="fw6"
              style={{ marginBottom: "5px", display: "block" }}
            >
              Parent School Contact Email
            </label>
            <Input
              placeholder="Parent school email"
              value={
                user.mail ||
                user.schoolInfo?.ContactEmail ||
                user.ContactEmail ||
                user.email ||
                user.contactEmail ||
                ""
              }
              disabled={true}
              fluid
              style={{ backgroundColor: "#f5f5f5", color: "#666" }}
            />
          </div> */}

          <div>
            <label
              className="fw6"
              style={{ marginBottom: "5px", display: "block" }}
            >
              {translation.typeLabel}
            </label>
            <Select
              showSearch
              placeholder={translation.typePlaceholder}
              optionFilterProp="children"
              onChange={(value) => {
                setFormData({ ...formData, type: value });
              }}
              value={
                formData.type
                  ? { value: formData.type, label: formData.type }
                  : undefined
              }
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={typeOptions.map((r) => ({
                value: r,
                label: r,
              }))}
              disabled={isLoading}
              style={{ width: "100%" }}
              getPopupContainer={(trigger) => trigger.parentElement}
            />
          </div>

          <div>
            <label
              className="fw6"
              style={{ marginBottom: "5px", display: "block" }}
            >
              {translation.categoryLabel}
            </label>
            <Select
              showSearch
              placeholder={translation.categoryPlaceholder}
              optionFilterProp="children"
              onChange={(value) => {
                setFormData({ ...formData, category: value });
              }}
              value={
                formData.category
                  ? { value: formData.category, label: formData.category }
                  : undefined
              }
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={categoryOptions.map((r) => ({
                value: r,
                label: r,
              }))}
              disabled={isLoading}
              style={{ width: "100%" }}
              getPopupContainer={(trigger) => trigger.parentElement}
            />
          </div>

          <div>
            <label
              className="fw6"
              style={{ marginBottom: "5px", display: "block" }}
            >
              {translation.descriptionLabel}
            </label>
            <TextArea
              placeholder={translation.descriptionPlaceholder}
              value={formData.description}
              onChange={(e, { value }) =>
                handleInputChange("description", value)
              }
              disabled={isLoading}
              fluid
              rows={3}
            />
          </div>
        </Flex>
      }
    />
  );
};

export default SchoolBranchForm;
